import { createRoot } from "react-dom/client";
// @ts-ignore
import * as Sentry from "@sentry/react";
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from "react-router";
import TagManager from "react-gtm-module";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "./index.css";
import { AuthProvider, ProtectedRoute } from "./AuthProvider";
import { ApprovedUserRoute, DetailRegisterRoute } from "./UserRouteUtil";
import NotLoginLayout from "./layouts/NotLoginLayout.tsx";
import Login from "./pages/Login/Login.tsx";
import Register from "./pages/Register/Register.tsx";
import AuthAction from "./pages/AuthAction/AuthAction.tsx";
import EmailVerification from "./pages/AuthAction/EmailVerification.tsx";
import PasswordReset from "./pages/AuthAction/PasswordReset.tsx";
import LoginLayout from "./layouts/LoginLayout.tsx";
import MyPage from "./pages/MyPage/Index/MyPage.tsx";
import DetailRegister from "./pages/DetailRegister/DetailRegister.tsx";
import DetailRegisterConfirm from "./pages/DetailRegister/Confirm/DetailRegisterConfirm.tsx";
import InfoChangeRequest from "./pages/MyPage/InfoChangeRequest/InfoChangeRequest.tsx";
import { FactoringProvider } from "./pages/FactoringEntry/FactoringProvider.tsx";
import FactoringEntry from "./pages/FactoringEntry/Index/FactoringEntry.tsx";
import FactoringConfirm from "./pages/FactoringEntry/Confirm/FactoringConfirm.tsx";
import FactoringReset from "./pages/FactoringEntry/Confirm/FactoringReset.tsx";
import { DetailRegisterProvider } from "./pages/DetailRegister/DetailRegisterProvider.tsx";
import RegisterComplete from "./pages/Register/RegisterComplete.tsx";

const router = createBrowserRouter([
  {
    index: true,
    element: <Navigate to="/login" />,
  },
  {
    Component: NotLoginLayout,
    children: [
      {
        path: "register",
        children: [
          {
            index: true,
            Component: Register,
          },
          {
            path: "complete",
            Component: RegisterComplete,
          },
        ],
      },
      {
        path: "login",
        children: [
          {
            index: true,
            Component: Login,
          },
        ],
      },
      {
        path: "auth/action",
        children: [
          {
            index: true,
            Component: AuthAction,
          },
          {
            path: "emailverify",
            Component: EmailVerification,
          },
          {
            path: "passwdreset",
            Component: PasswordReset,
          },
        ],
      },
    ],
  },
  {
    element: (
      <AuthProvider>
        <ProtectedRoute />
      </AuthProvider>
    ),
    children: [
      {
        Component: LoginLayout,
        children: [
          {
            path: "mypage",
            children: [
              {
                index: true,
                Component: MyPage,
              },
              {
                path: "detail/register",
                element: (
                  <DetailRegisterRoute />
                ),
                children: [
                  {
                    element: (
                      <DetailRegisterProvider>
                        <Outlet />
                      </DetailRegisterProvider>
                    ),
                    children: [
                      {
                        index: true,
                        Component: DetailRegister,
                      },
                      {
                        path: 'confirm',
                        Component: DetailRegisterConfirm,
                      },
                    ]
                  }
                ],
              },
            ],
          },
          {
            path: "factoring",
            element: (
              <ApprovedUserRoute />
            ),
            children: [
              {
                element: (
                  <FactoringProvider>
                    <Outlet />
                  </FactoringProvider>
                ),
                children: [
                  {
                    path: "entry",
                    Component: FactoringEntry,
                  },
                  {
                    path: "confirm",
                    Component: FactoringConfirm,
                  },
                  {
                    path: "reset",
                    Component: FactoringReset,
                  },
                ],
              },
            ],
          },
          {
            path: "changereq",
            element: (
              <ApprovedUserRoute />
            ),
            children: [
              {
                index: true,
                Component: InfoChangeRequest,
              },
            ],
          },
        ],
      },
    ],
  },
]);


const theme = createTheme({
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #5e85b8, #cc6496)',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        caption: {
          fontSize: '0.9rem',
        },
      },
    },
  },
});

if (typeof import.meta.env.VITE_GTM_ID === 'string' && import.meta.env.VITE_GTM_ID.length > 0) {
  const tagManagerArgs = {
    gtmId: import.meta.env.VITE_GTM_ID,
  }

  TagManager.initialize(tagManagerArgs)

  if (import.meta.env.DEV) {
      console.log("Google tag manager initialized.");
  }
}

// Sentry.init({
//   dsn: import.meta.env.VITE_SENTRY_DSN,
//   integrations: [
//     Sentry.browserTracingIntegration(),
//     Sentry.replayIntegration(),
//   ],
//   // Performance Monitoring
//   tracesSampleRate: 1.0, // Capture 100% of the transactions
//   // Session Replay
//   replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%.
//   replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, sample 100% of sessions where errors occur.
//
//   environment: import.meta.env.MODE,
//   // Setting this option to true will send default PII data to Sentry.
//   // For example, automatic IP address collection on events
//   sendDefaultPii: true
// });

const root = document.getElementById("root");

// const option = {
//   // Callback called when an error is thrown and not caught by an ErrorBoundary.
//   onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
//     console.warn('Uncaught error', error, errorInfo.componentStack);
//   }),
//   // Callback called when React catches an error in an ErrorBoundary.
//   onCaughtError: Sentry.reactErrorHandler(),
//   // Callback called when React automatically recovers from errors.
//   onRecoverableError: Sentry.reactErrorHandler(),
// }

createRoot(root!).render(
  <>
    <CssBaseline />
    <ThemeProvider theme={theme}>
      <RouterProvider router={router} />
    </ThemeProvider>
  </>,
);
